import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftOutlined, ReloadOutlined, DeleteOutlined, LoadingOutlined, RobotOutlined,
  ThunderboltOutlined, FileTextOutlined, ApiOutlined, BarChartOutlined, CheckCircleOutlined,
  ExclamationCircleOutlined, ClockCircleOutlined, PauseCircleOutlined, SettingOutlined,
  CopyOutlined, InfoCircleOutlined, FilePdfOutlined,
  EyeOutlined, EyeInvisibleOutlined, KeyOutlined, UploadOutlined, CloudUploadOutlined,
  FileOutlined, CloseCircleOutlined, GlobalOutlined, PlusOutlined, ExportOutlined,
} from '@ant-design/icons';
import { servicesService } from '../../services';
import { assistantService } from '../../services/assistant.service';
import { ConfirmationModal, DetailPageHeader, HeaderActionMenu } from '../../components/shared';
import { getServiceAge } from '../../utils/serviceHelpers';
import { useNotificationContext } from '../../contexts/NotificationContext';
import { AssistantDetail, DocumentItem, WidgetConfigUpdate } from '../../types/chatbot.type';


const STATUS_CONFIG: Record<string, { label: string; color: string; bgColor: string; icon: React.ReactNode }> = {
  running: { label: 'Activo', color: 'text-green-800 dark:text-green-400', bgColor: 'bg-green-100 dark:bg-green-900/30', icon: <CheckCircleOutlined /> },
  active: { label: 'Activo', color: 'text-green-800 dark:text-green-400', bgColor: 'bg-green-100 dark:bg-green-900/30', icon: <CheckCircleOutlined /> },
  stopped: { label: 'Detenido', color: 'text-gray-800 dark:text-gray-300', bgColor: 'bg-gray-100 dark:bg-gray-700', icon: <PauseCircleOutlined /> },
  deploying: { label: 'Desplegando', color: 'text-blue-800 dark:text-blue-400', bgColor: 'bg-blue-100 dark:bg-blue-900/30', icon: <ClockCircleOutlined /> },
  error: { label: 'Error', color: 'text-red-800 dark:text-red-400', bgColor: 'bg-red-100 dark:bg-red-900/30', icon: <ExclamationCircleOutlined /> },
};

const PLAN_INFO: Record<string, { name: string; price: number; color: string }> = {
  starter: { name: 'Starter', price: 29, color: 'text-blue-600 dark:text-blue-400' },
  pro: { name: 'Pro', price: 99, color: 'text-purple-600 dark:text-purple-400' },
  enterprise: { name: 'Enterprise', price: 299, color: 'text-amber-600 dark:text-amber-400' },
};

const ALLOWED_EXTENSIONS = ['.pdf', '.txt', '.docx'];
const MAX_FILE_SIZE = 25 * 1024 * 1024;

const WIDGET_MODES = [
  { value: 'bubble' as const, label: 'Burbuja Flotante', desc: 'Esquina inferior derecha' },
  { value: 'sidebar' as const, label: 'Sidebar', desc: 'Panel lateral deslizable' },
  { value: 'bottombar' as const, label: 'Barra Inferior', desc: 'Panel fijo abajo' },
  { value: 'inline' as const, label: 'Inline', desc: 'Embebido en un div' },
];

const ChatbotDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { success, error: showError } = useNotificationContext();

  const [service, setService] = useState<AssistantDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'documents' | 'usage' | 'settings'>('overview');
  const [showApiKey, setShowApiKey] = useState(false);
  const [showSecretKeyModal, setShowSecretKeyModal] = useState(false);
  const [generatedSecretKey, setGeneratedSecretKey] = useState<string | null>(null);
  const [isGeneratingSecret, setIsGeneratingSecret] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<Record<string, 'pending' | 'uploading' | 'done' | 'error'>>({});
  const [dragActive, setDragActive] = useState(false);
  const [widgetConfig, setWidgetConfig] = useState<WidgetConfigUpdate>({
    botName: '',
    primaryColor: '#6366f1',
    secondaryColor: '',
    welcomeMessage: '',
    placeholder: '',
    widgetMode: 'bubble',
    systemPrompt: '',
  });
  const [isSavingWidget, setIsSavingWidget] = useState(false);
  const [documents, setDocuments] = useState<DocumentItem[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [deletingDoc, setDeletingDoc] = useState<string | null>(null);
  const [showDeleteDocModal, setShowDeleteDocModal] = useState(false);
  const [docToDelete, setDocToDelete] = useState<DocumentItem | null>(null);
  const [allowedOrigins, setAllowedOrigins] = useState<string[]>([]);
  const [newOrigin, setNewOrigin] = useState('');
  const [isSavingOrigins, setIsSavingOrigins] = useState(false);

  useEffect(() => { fetchService(); }, [id]);
  useEffect(() => { if (activeTab === 'documents' && service) fetchDocuments(); }, [activeTab, service?.id]);

  const fetchService = async (showLoader = true) => {
    if (!id) { setError('No se proporcionó un ID de servicio'); setLoading(false); return; }
    try {
      if (showLoader && !service) setLoading(true);
      setError(null);
      const data = await assistantService.getDetail(id);
      setService(data);
      if (data.template) {
        setWidgetConfig({
          botName: data.template.botName || data.name || '',
          primaryColor: data.template.primaryColor || '#6366f1',
          secondaryColor: data.template.secondaryColor || '',
          welcomeMessage: data.template.welcomeMessage || '¡Hola! ¿En qué puedo ayudarte?',
          placeholder: data.template.placeholder || 'Escribe tu mensaje...',
          widgetMode: data.template.widgetMode || 'bubble',
          systemPrompt: data.template.systemPrompt || '',
        });
        setAllowedOrigins(data.allowedOrigins || []);
      }
    } catch (err: any) {
      console.error('Error fetching chatbot service:', err);
      if (!service) setError(err.message || 'Error al cargar el servicio');
      showError('Error al cargar el chatbot');
    } finally { setLoading(false); }
  };

  const handleDelete = async () => {
    if (!service) return;
    try {
      setIsDeleting(true);
      await servicesService.delete(service.id);
      success('Chatbot eliminado correctamente');
      navigate('/chatbot-ia');
    } catch (err: any) {
      console.error('Error deleting chatbot:', err);
      showError(err.message || 'Error al eliminar el chatbot');
    } finally { setIsDeleting(false); setShowDeleteModal(false); }
  };

  const handleGenerateSecretKey = async () => {
    if (!service) return;
    try {
      setIsGeneratingSecret(true);
      const result = await assistantService.generateSecretKey(service.apiKey, service.tenantId);
      setGeneratedSecretKey(result.secretKey);
      setShowSecretKeyModal(true);
      fetchService(false);
    } catch (err: any) {
      console.error('Error generating secret key:', err);
      showError(err.message || 'Error al generar la Secret Key');
    } finally { setIsGeneratingSecret(false); }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    success(`${label} copiado al portapapeles`);
  };

  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation(); setDragActive(false);
    addFiles(Array.from(e.dataTransfer.files));
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) { addFiles(Array.from(e.target.files)); e.target.value = ''; }
  };

  const addFiles = (files: File[]) => {
    const validFiles = files.filter(file => {
      const ext = '.' + file.name.split('.').pop()?.toLowerCase();
      if (!ALLOWED_EXTENSIONS.includes(ext)) { showError(`Archivo no soportado: ${file.name}. Solo PDF, TXT y DOCX.`); return false; }
      if (file.size > MAX_FILE_SIZE) { showError(`Archivo muy grande: ${file.name}. Máximo 25 MB.`); return false; }
      return true;
    });
    setUploadingFiles(prev => [...prev, ...validFiles]);
  };

  const removeFile = (index: number) => { setUploadingFiles(prev => prev.filter((_, i) => i !== index)); };

  const handleUpload = async () => {
    if (!service || uploadingFiles.length === 0) return;
    const remaining = service.documentsLimit - service.documentsCount;
    if (uploadingFiles.length > remaining) { showError(`Solo puedes subir ${remaining} documento(s) más. Límite: ${service.documentsLimit}`); return; }
    setIsUploading(true);
    const progress: Record<string, 'pending' | 'uploading' | 'done' | 'error'> = {};
    uploadingFiles.forEach(f => { progress[f.name] = 'pending'; });
    setUploadProgress({ ...progress });
    let successCount = 0;
    for (const file of uploadingFiles) {
      try {
        progress[file.name] = 'uploading'; setUploadProgress({ ...progress });
        const uploadRequest = await assistantService.requestUpload(service.id, file.name, file.size);
        const uploadResponse = await fetch(uploadRequest.uploadUrl, { method: 'PUT', headers: { 'Content-Type': uploadRequest.contentType }, body: file });
        if (!uploadResponse.ok) throw new Error(`Error al subir ${file.name}`);
        progress[file.name] = 'done'; setUploadProgress({ ...progress }); successCount++;
      } catch (err: any) {
        console.error(`Error uploading ${file.name}:`, err);
        progress[file.name] = 'error'; setUploadProgress({ ...progress });
      }
    }
    setIsUploading(false);
    if (successCount > 0) {
      success(`${successCount} documento(s) subido(s) correctamente. Sincronizando Knowledge Base...`);
      setUploadingFiles(prev => prev.filter(f => progress[f.name] !== 'done'));
      setUploadProgress({}); fetchService(false);
    }
  };

  const handleSaveWidgetConfig = async () => {
    if (!service) return;
    try {
      setIsSavingWidget(true);
      await assistantService.updateWidgetConfig(service.apiKey, service.tenantId, widgetConfig);
      success('Configuración del widget actualizada');
      fetchService(false);
    } catch (err: any) {
      showError(err.message || 'Error al guardar la configuración');
    } finally {
      setIsSavingWidget(false);
    }
  };

  const fetchDocuments = async () => {
    if (!service) return;
    try {
      setLoadingDocs(true);
      const data = await assistantService.listDocuments(service.id);
      setDocuments(data.documents || []);
    } catch (err: any) {
      console.error('Error fetching documents:', err);
    } finally {
      setLoadingDocs(false);
    }
  };

  const confirmDeleteDoc = (doc: DocumentItem) => {
    setDocToDelete(doc);
    setShowDeleteDocModal(true);
  };

  const handleDeleteDocument = async () => {
    if (!service || !docToDelete) return;
    try {
      setDeletingDoc(docToDelete.filename);
      setShowDeleteDocModal(false);
      await assistantService.deleteDocument(service.id, docToDelete.filename);
      success(`"${docToDelete.filename}" eliminado. Sincronizando Knowledge Base...`);
      setDocToDelete(null);
      fetchDocuments();
      fetchService(false);
    } catch (err: any) {
      console.error('Error deleting document:', err);
      showError(err.message || 'Error al eliminar el documento');
    } finally {
      setDeletingDoc(null);
    }
  };

  const handleSaveOrigins = async () => {
    if (!service) return;
    try {
      setIsSavingOrigins(true);
      await assistantService.updateWidgetConfig(service.apiKey, service.tenantId, { ...widgetConfig, allowedOrigins });
      success('Orígenes permitidos actualizados');
      fetchService(false);
    } catch (err: any) {
      showError(err.message || 'Error al guardar los orígenes');
    } finally { setIsSavingOrigins(false); }
  };

  const addOrigin = () => {
    const origin = newOrigin.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/+$/, '');
    if (!origin) return;
    if (allowedOrigins.includes(origin)) { showError('Este dominio ya está en la lista'); return; }
    if (allowedOrigins.length >= 20) { showError('Máximo 20 dominios permitidos'); return; }
    setAllowedOrigins(prev => [...prev, origin]);
    setNewOrigin('');
  };

  const removeOrigin = (index: number) => {
    setAllowedOrigins(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileIcon = (filename: string) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    if (ext === 'pdf') return <FilePdfOutlined className="text-red-500" />;
    if (ext === 'docx' || ext === 'doc') return <FileTextOutlined className="text-blue-500" />;
    return <FileOutlined className="text-gray-400" />;
  };

  if (loading) {
    return (
      <div className="max-w-[1400px] mx-auto px-8 py-6">
        <div className="text-center py-16">
          <LoadingOutlined className="text-4xl text-purple-500 mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Cargando chatbot...</p>
        </div>
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="max-w-[1400px] mx-auto px-8 py-6">
        <div className="text-center py-16">
          <ExclamationCircleOutlined className="text-4xl text-red-500 mb-4" />
          <p className="text-gray-600 dark:text-gray-400">{error || 'Servicio no encontrado'}</p>
          <button onClick={() => navigate('/chatbot-ia')} className="mt-4 px-4 py-2 bg-black dark:bg-white text-white dark:text-black rounded-md hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors">
            Volver al listado
          </button>
        </div>
      </div>
    );
  }

  const normalizedStatus = service.status?.toLowerCase();
  const statusConfig = STATUS_CONFIG[normalizedStatus] || STATUS_CONFIG.error;
  const planInfo = PLAN_INFO[service.plan] || PLAN_INFO.starter;
  const messagesUsagePercent = service.messagesLimit > 0 ? Math.min((service.messagesUsed / service.messagesLimit) * 100, 100) : 0;
  const documentsUsagePercent = service.documentsLimit > 0 ? Math.min((service.documentsCount / service.documentsLimit) * 100, 100) : 0;

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
      <DetailPageHeader
        backLabel="Volver"
        onBack={() => navigate('/chatbot-ia')}
        title={service.name}
        subtitle={`${planInfo.name} • Creado ${getServiceAge(new Date(service.createdAt))}`}
        icon={
          <div className="w-full h-full bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
            <RobotOutlined className="text-2xl text-purple-600 dark:text-purple-400" />
          </div>
        }
        actions={
          <>
            <HeaderActionMenu items={[
              { key: 'refresh', label: 'Refrescar', icon: <ReloadOutlined className={loading ? 'animate-spin' : ''} />, onClick: () => fetchService(), disabled: loading },
              { key: 'delete', label: 'Eliminar', icon: <DeleteOutlined />, onClick: () => setShowDeleteModal(true), danger: true },
            ]} />
          </>
        }
      />

      {/* Status Badge */}
      <div className="mb-6">
        <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg ${statusConfig.bgColor} ${statusConfig.color} text-sm font-medium`}>
          {statusConfig.icon}{statusConfig.label}
        </span>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <div className="flex gap-6">
          {(['overview', 'documents', 'usage', 'settings'] as const).map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${activeTab === tab ? 'border-black dark:border-white text-black dark:text-white' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`}>
              {{ overview: 'Información General', documents: 'Documentos', usage: 'Uso y Estadísticas', settings: 'Configuración' }[tab]}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-8 space-y-6">

          {/* ==================== OVERVIEW TAB ==================== */}
          {activeTab === 'overview' && (
            <>
              {/* Plan Information */}
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Información del Plan</h2>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-3 gap-6">
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">Plan Actual</label>
                      <p className={`text-xl font-bold ${planInfo.color}`}>{planInfo.name}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">${planInfo.price}/mes</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">Mensajes/mes</label>
                      <p className="text-xl font-bold text-gray-900 dark:text-white">{service.messagesLimit.toLocaleString()}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400 mb-1 block">Documentos</label>
                      <p className="text-xl font-bold text-gray-900 dark:text-white">{service.documentsLimit.toLocaleString()}</p>
                    </div>
                  </div>
                </div>
              </div>

              {service.description && (
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                  <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Descripción</h2>
                  </div>
                  <div className="p-6"><p className="text-gray-700 dark:text-gray-300">{service.description}</p></div>
                </div>
              )}
            </>
          )}

          {/* ==================== DOCUMENTS TAB ==================== */}
          {activeTab === 'documents' && (
            <>
              {/* Upload Section */}
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Subir Documentos</h2>
                  <span className="text-sm text-gray-500 dark:text-gray-400">{service.documentsCount} / {service.documentsLimit} documentos</span>
                </div>
                <div className="p-6">
                  <div
                    onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                    onDragLeave={() => setDragActive(false)}
                    onDrop={handleFileDrop}
                    className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${dragActive ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20' : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'}`}
                  >
                    <CloudUploadOutlined className={`text-4xl mb-3 ${dragActive ? 'text-purple-500' : 'text-gray-400 dark:text-gray-500'}`} />
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Arrastra archivos aquí o{' '}
                      <label className="text-purple-600 dark:text-purple-400 cursor-pointer hover:underline">
                        selecciona archivos
                        <input type="file" multiple accept=".pdf,.txt,.docx" onChange={handleFileSelect} className="hidden" />
                      </label>
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">PDF, TXT, DOCX — Máximo 25 MB por archivo</p>
                  </div>

                  {uploadingFiles.length > 0 && (
                    <div className="mt-4 space-y-2">
                      {uploadingFiles.map((file, index) => {
                        const status = uploadProgress[file.name];
                        return (
                          <div key={`${file.name}-${index}`} className="flex items-center gap-3 px-4 py-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                            <FileOutlined className="text-gray-400 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{file.name}</p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">{(file.size / 1024).toFixed(1)} KB</p>
                            </div>
                            {status === 'uploading' && <LoadingOutlined className="text-purple-500" />}
                            {status === 'done' && <CheckCircleOutlined className="text-green-500" />}
                            {status === 'error' && <ExclamationCircleOutlined className="text-red-500" />}
                            {!status && (
                              <button onClick={() => removeFile(index)} className="p-1 text-gray-400 hover:text-red-500 transition-colors"><CloseCircleOutlined /></button>
                            )}
                          </div>
                        );
                      })}
                      <button onClick={handleUpload} disabled={isUploading || uploadingFiles.length === 0}
                        className="w-full mt-3 px-4 py-2.5 bg-black dark:bg-white text-white dark:text-black rounded-lg text-sm font-medium hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                        {isUploading ? (<><LoadingOutlined /> Subiendo...</>) : (<><UploadOutlined /> Subir {uploadingFiles.length} archivo{uploadingFiles.length !== 1 ? 's' : ''}</>)}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Documents List */}
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Documentos Cargados</h2>
                  <button onClick={fetchDocuments} disabled={loadingDocs} className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 flex items-center gap-1">
                    <ReloadOutlined className={loadingDocs ? 'animate-spin' : ''} /> Refrescar
                  </button>
                </div>
                <div className="p-6">
                  {loadingDocs ? (
                    <div className="text-center py-8">
                      <LoadingOutlined className="text-2xl text-purple-500 mb-2" />
                      <p className="text-sm text-gray-500 dark:text-gray-400">Cargando documentos...</p>
                    </div>
                  ) : documents.length === 0 ? (
                    <div className="text-center py-8">
                      <FileTextOutlined className="text-3xl text-gray-300 dark:text-gray-600 mb-2" />
                      <p className="text-sm text-gray-500 dark:text-gray-400">No hay documentos cargados aún</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Sube archivos arriba para alimentar tu Knowledge Base</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {documents.map((doc) => (
                        <div key={doc.key} className="flex items-center gap-3 px-4 py-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                          <span className="text-lg flex-shrink-0">{getFileIcon(doc.filename)}</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{doc.filename}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {formatFileSize(doc.size)} • {new Date(doc.lastModified).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                          <button
                            onClick={() => confirmDeleteDoc(doc)}
                            disabled={deletingDoc === doc.filename}
                            className="p-2 text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors disabled:opacity-50"
                            title="Eliminar documento"
                          >
                            {deletingDoc === doc.filename ? <LoadingOutlined /> : <DeleteOutlined />}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-lg border border-purple-200 dark:border-purple-800 p-6">
                <div className="flex items-start gap-3">
                  <InfoCircleOutlined className="text-xl text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Sobre los documentos</h3>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      Los documentos que subas alimentan la base de conocimientos de tu asistente.
                      Después de subir un archivo, la sincronización se realiza automáticamente
                      y puede tardar unos segundos. Tu asistente podrá responder preguntas basándose
                      en el contenido de tus documentos.
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* ==================== USAGE TAB ==================== */}
          {activeTab === 'usage' && (
            <>
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Uso de Recursos</h2>
                </div>
                <div className="p-6 space-y-6">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <ThunderboltOutlined className="text-gray-500 dark:text-gray-400" />
                        <span className="text-sm font-medium text-gray-900 dark:text-white">Mensajes este mes</span>
                      </div>
                      <span className="text-sm font-semibold text-gray-900 dark:text-white">{service.messagesUsed.toLocaleString()} / {service.messagesLimit.toLocaleString()}</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                      <div className={`h-3 rounded-full transition-all ${messagesUsagePercent > 90 ? 'bg-red-500' : messagesUsagePercent > 70 ? 'bg-yellow-500' : 'bg-green-500'}`} style={{ width: `${messagesUsagePercent}%` }}></div>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{messagesUsagePercent.toFixed(1)}% utilizado</p>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <FileTextOutlined className="text-gray-500 dark:text-gray-400" />
                        <span className="text-sm font-medium text-gray-900 dark:text-white">Documentos cargados</span>
                      </div>
                      <span className="text-sm font-semibold text-gray-900 dark:text-white">{service.documentsCount} / {service.documentsLimit}</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                      <div className={`h-3 rounded-full transition-all ${documentsUsagePercent > 90 ? 'bg-red-500' : documentsUsagePercent > 70 ? 'bg-yellow-500' : 'bg-green-500'}`} style={{ width: `${documentsUsagePercent}%` }}></div>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{documentsUsagePercent.toFixed(1)}% utilizado</p>
                  </div>
                </div>
              </div>

            </>
          )}

          {/* ==================== SETTINGS TAB ==================== */}
          {activeTab === 'settings' && (
            <>
              {/* API Keys */}
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">API Keys</h2>
                </div>
                <div className="p-6 space-y-6">
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                      API Key<span className="ml-2 text-xs font-normal text-gray-400">(pública — para el widget)</span>
                    </label>
                    <div className="flex items-center gap-2">
                      <input type={showApiKey ? 'text' : 'password'} value={service.apiKey || ''} readOnly className="flex-1 px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-mono text-gray-900 dark:text-white" />
                      <button onClick={() => setShowApiKey(!showApiKey)} className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200" title={showApiKey ? 'Ocultar' : 'Mostrar'}>
                        {showApiKey ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                      </button>
                      <button onClick={() => copyToClipboard(service.apiKey || '', 'API Key')} className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200" title="Copiar"><CopyOutlined /></button>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Usa esta clave en el widget embebido en tu sitio web</p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">Snippet de instalación</label>
                    <div className="relative">
                      <pre className="px-4 py-3 bg-gray-900 text-green-400 rounded-lg text-xs font-mono overflow-x-auto">
                        {`<script src="https://dxy0mpvb3evn5.cloudfront.net/widget/kubiy-assistant.min.js"
  data-api-key="${service.apiKey || 'tu-api-key'}">
</script>`}
                      </pre>
                      <button onClick={() => copyToClipboard(`<script src="https://dxy0mpvb3evn5.cloudfront.net/widget/kubiy-assistant.min.js"\n  data-api-key="${service.apiKey || ''}"></script>`, 'Snippet')} className="absolute top-2 right-2 p-1.5 bg-gray-700 hover:bg-gray-600 rounded text-gray-300 hover:text-white" title="Copiar snippet">
                        <CopyOutlined className="text-xs" />
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Pega este código en tu sitio web para mostrar el asistente</p>
                  </div>

                  <div className="border-t border-gray-200 dark:border-gray-700"></div>

                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                      Secret Key<span className="ml-2 text-xs font-normal text-gray-400">(privada — para integraciones server-to-server)</span>
                    </label>
                    {service.hasSecretKey ? (
                      <div className="flex items-center gap-3 px-4 py-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                        <CheckCircleOutlined className="text-green-600 dark:text-green-400" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-green-800 dark:text-green-300">Secret Key activa</p>
                          <p className="text-xs text-green-600 dark:text-green-400">Ya tienes una Secret Key generada. Si la perdiste, puedes generar una nueva (la anterior se invalidará).</p>
                        </div>
                        <button onClick={handleGenerateSecretKey} disabled={isGeneratingSecret} className="px-3 py-1.5 text-xs font-medium border border-yellow-400 dark:border-yellow-600 text-yellow-700 dark:text-yellow-400 rounded-md hover:bg-yellow-50 dark:hover:bg-yellow-900/20 transition-colors disabled:opacity-50">
                          {isGeneratingSecret ? <LoadingOutlined /> : 'Regenerar'}
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg">
                        <KeyOutlined className="text-gray-400" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Sin Secret Key</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Genera una Secret Key para usar los endpoints de chat y upload desde tu servidor</p>
                        </div>
                        <button onClick={handleGenerateSecretKey} disabled={isGeneratingSecret} className="px-4 py-2 text-sm font-medium bg-black dark:bg-white text-white dark:text-black rounded-md hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors disabled:opacity-50 flex items-center gap-2">
                          {isGeneratingSecret ? <LoadingOutlined /> : <><KeyOutlined /> Generar Secret Key</>}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Allowed Origins */}
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Orígenes Permitidos</h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Dominios desde donde se puede usar el widget</p>
                  </div>
                  <button onClick={handleSaveOrigins} disabled={isSavingOrigins} className="px-4 py-2 bg-black dark:bg-white text-white dark:text-black rounded-md text-sm font-medium hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors disabled:opacity-50 flex items-center gap-2">
                    {isSavingOrigins && <LoadingOutlined />}Guardar
                  </button>
                </div>
                <div className="p-6 space-y-4">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 relative">
                      <GlobalOutlined className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                      <input type="text" value={newOrigin} onChange={(e) => setNewOrigin(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addOrigin()} className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white" placeholder="ejemplo.com" />
                    </div>
                    <button onClick={addOrigin} disabled={!newOrigin.trim()} className="px-4 py-2 bg-black dark:bg-white text-white dark:text-black rounded-md text-sm font-medium hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors disabled:opacity-50 flex items-center gap-2">
                      <PlusOutlined /> Agregar
                    </button>
                  </div>
                  {allowedOrigins.length === 0 ? (
                    <div className="text-center py-6">
                      <GlobalOutlined className="text-3xl text-gray-300 dark:text-gray-600 mb-2" />
                      <p className="text-sm text-gray-500 dark:text-gray-400">Sin restricción de dominio</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">El widget funcionará desde cualquier sitio web</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {allowedOrigins.map((origin, index) => (
                        <div key={origin} className="flex items-center gap-3 px-4 py-2.5 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                          <GlobalOutlined className="text-green-500 flex-shrink-0" />
                          <span className="flex-1 text-sm font-mono text-gray-900 dark:text-white">{origin}</span>
                          <button onClick={() => removeOrigin(index)} className="p-1 text-gray-400 hover:text-red-500 transition-colors" title="Eliminar"><DeleteOutlined /></button>
                        </div>
                      ))}
                      <p className="text-xs text-gray-500 dark:text-gray-400">{allowedOrigins.length} de 20 dominios • Los subdominios (www.ejemplo.com) se permiten automáticamente</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Widget Configuration */}
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Configuración del Widget</h2>
                  <button
                    onClick={handleSaveWidgetConfig}
                    disabled={isSavingWidget}
                    className="px-4 py-2 bg-black dark:bg-white text-white dark:text-black rounded-md text-sm font-medium hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    {isSavingWidget && <LoadingOutlined />}
                    Guardar cambios
                  </button>
                </div>
                <div className="p-6 space-y-6">
                  {/* Modo del Widget */}
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 block">Modo de presentación</label>
                    <div className="grid grid-cols-2 gap-3">
                      {WIDGET_MODES.map(mode => (
                        <button
                          key={mode.value}
                          onClick={() => setWidgetConfig(prev => ({ ...prev, widgetMode: mode.value }))}
                          className={`p-4 rounded-lg border-2 text-left transition-all ${widgetConfig.widgetMode === mode.value
                            ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                            : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                            }`}
                        >
                          <p className="font-medium text-sm text-gray-900 dark:text-white">{mode.label}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{mode.desc}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Color Primario */}
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">Color primario</label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={widgetConfig.primaryColor}
                        onChange={(e) => setWidgetConfig(prev => ({ ...prev, primaryColor: e.target.value }))}
                        className="w-10 h-10 rounded-lg border border-gray-300 dark:border-gray-600 cursor-pointer p-0"
                      />
                      <input
                        type="text"
                        value={widgetConfig.primaryColor}
                        onChange={(e) => setWidgetConfig(prev => ({ ...prev, primaryColor: e.target.value }))}
                        className="w-28 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-mono bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                      <div className="w-24 h-10 rounded-lg" style={{ background: widgetConfig.primaryColor }}></div>
                    </div>
                  </div>

                  {/* Color Secundario */}
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                      Color secundario
                      <span className="ml-2 text-xs font-normal text-gray-400">(opcional — crea un gradiente)</span>
                    </label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={widgetConfig.secondaryColor || widgetConfig.primaryColor}
                        onChange={(e) => setWidgetConfig(prev => ({ ...prev, secondaryColor: e.target.value }))}
                        className="w-10 h-10 rounded-lg border border-gray-300 dark:border-gray-600 cursor-pointer p-0"
                      />
                      <input
                        type="text"
                        value={widgetConfig.secondaryColor}
                        onChange={(e) => setWidgetConfig(prev => ({ ...prev, secondaryColor: e.target.value }))}
                        className="w-28 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-mono bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="Sin gradiente"
                      />
                      {widgetConfig.secondaryColor && (
                        <>
                          <div className="w-24 h-10 rounded-lg" style={{ background: `linear-gradient(135deg, ${widgetConfig.primaryColor}, ${widgetConfig.secondaryColor})` }}></div>
                          <button
                            onClick={() => setWidgetConfig(prev => ({ ...prev, secondaryColor: '' }))}
                            className="text-xs text-gray-400 hover:text-red-500 transition-colors"
                          >
                            Quitar
                          </button>
                        </>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Déjalo vacío para usar color sólido</p>
                  </div>

                  {/* Nombre del Bot */}
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">Nombre del asistente</label>
                    <input
                      type="text"
                      value={widgetConfig.botName}
                      onChange={(e) => setWidgetConfig(prev => ({ ...prev, botName: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Asistente"
                      maxLength={50}
                    />
                  </div>

                  {/* Mensaje de Bienvenida */}
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">Mensaje de bienvenida</label>
                    <textarea
                      value={widgetConfig.welcomeMessage}
                      onChange={(e) => setWidgetConfig(prev => ({ ...prev, welcomeMessage: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                      rows={2}
                      placeholder="¡Hola! ¿En qué puedo ayudarte?"
                      maxLength={200}
                    />
                  </div>

                  {/* Placeholder */}
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">Placeholder del campo de texto</label>
                    <input
                      type="text"
                      value={widgetConfig.placeholder}
                      onChange={(e) => setWidgetConfig(prev => ({ ...prev, placeholder: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Escribe tu mensaje..."
                      maxLength={100}
                    />
                  </div>

                  {/* System Prompt */}
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                      Instrucciones del asistente
                      <span className="ml-2 text-xs font-normal text-gray-400">(contexto que la IA siempre tendrá en cuenta)</span>
                    </label>
                    <textarea
                      value={widgetConfig.systemPrompt}
                      onChange={(e) => setWidgetConfig(prev => ({ ...prev, systemPrompt: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                      rows={4}
                      placeholder="Ej: Eres un agente de ventas de la empresa XYZ. Siempre responde de forma amable y ofrece los productos del catálogo cuando sea relevante."
                      maxLength={1000}
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Define el rol, personalidad y reglas del asistente. Máximo 1000 caracteres. ({widgetConfig.systemPrompt?.length || 0}/1000)
                    </p>
                  </div>

                  {/* Preview en vivo */}
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">Vista previa</label>
                    <div className="border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
                      <div className="p-4 flex items-center gap-3" style={{ background: widgetConfig.secondaryColor ? `linear-gradient(135deg, ${widgetConfig.primaryColor}, ${widgetConfig.secondaryColor})` : widgetConfig.primaryColor }}>
                        <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                        <p className="text-white font-semibold text-sm">{widgetConfig.botName || 'Asistente'}</p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-900 p-4 space-y-3">
                        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg rounded-bl-sm px-3 py-2 text-sm text-gray-700 dark:text-gray-300 max-w-[80%]">
                          {widgetConfig.welcomeMessage || '¡Hola! ¿En qué puedo ayudarte?'}
                        </div>
                        <div className="rounded-lg rounded-br-sm px-3 py-2 text-sm text-white max-w-[80%] ml-auto" style={{ background: widgetConfig.secondaryColor ? `linear-gradient(135deg, ${widgetConfig.primaryColor}, ${widgetConfig.secondaryColor})` : widgetConfig.primaryColor }}>
                          ¿Cuáles son sus horarios?
                        </div>
                      </div>
                      <div className="bg-white dark:bg-gray-800 p-3 border-t border-gray-200 dark:border-gray-700">
                        <div className="border border-gray-300 dark:border-gray-600 rounded-full px-4 py-2 text-sm text-gray-400">
                          {widgetConfig.placeholder || 'Escribe tu mensaje...'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Administration */}
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Administración</h2>
                </div>
                <div className="p-6 space-y-3">
                  <button className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors text-left">
                    <div className="flex items-center gap-3">
                      <SettingOutlined className="text-gray-600 dark:text-gray-400" />
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">Configuración General</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Nombre, descripción y opciones básicas</p>
                      </div>
                    </div>
                  </button>
                  <button onClick={() => setShowDeleteModal(true)} className="w-full flex items-center justify-between px-4 py-3 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors text-left border border-red-200 dark:border-red-800">
                    <div className="flex items-center gap-3">
                      <DeleteOutlined className="text-red-600 dark:text-red-400" />
                      <div>
                        <p className="font-medium text-red-700 dark:text-red-400">Eliminar Chatbot</p>
                        <p className="text-xs text-red-600 dark:text-red-500">Esta acción no se puede deshacer</p>
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* ==================== RIGHT SIDEBAR ==================== */}
        <div className="col-span-4 space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">Información del Servicio</h3>
            </div>
            <div className="p-6 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">ID del Servicio</span>
                <div className="flex items-center gap-1">
                  <code className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded font-mono text-gray-900 dark:text-white">{service.id.slice(0, 8)}...</code>
                  <button onClick={() => copyToClipboard(service.id, 'ID')} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" title="Copiar ID"><CopyOutlined className="text-xs" /></button>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">Estado</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white capitalize">{statusConfig.label}</span>
              </div>
              <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Endpoints</span>
              </div>
              <div>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Chat</span>
                  <button onClick={() => copyToClipboard('https://api.kubiy.cloud/v1/assistant/chat', 'Endpoint Chat')} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" title="Copiar"><CopyOutlined className="text-xs" /></button>
                </div>
                <code className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded font-mono text-gray-900 dark:text-white block truncate">POST /assistant/chat</code>
              </div>
              <div>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Upload</span>
                  <button onClick={() => copyToClipboard('https://api.kubiy.cloud/v1/assistant/upload', 'Endpoint Upload')} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" title="Copiar"><CopyOutlined className="text-xs" /></button>
                </div>
                <code className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded font-mono text-gray-900 dark:text-white block truncate">POST /assistant/upload</code>
              </div>
              <div className="pt-2 border-t border-gray-200 dark:border-gray-700"></div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">Última actualización</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">{getServiceAge(new Date(service.updatedAt))}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">Creado</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">{getServiceAge(new Date(service.createdAt))}</span>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Acciones Rápidas</h3>
            <div className="space-y-2">
              <button className="w-full flex items-center justify-between px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                <span>Ver Widget</span><RobotOutlined />
              </button>
              <button onClick={() => setActiveTab('documents')} className="w-full flex items-center justify-between px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                <span>Subir Documentos</span><UploadOutlined />
              </button>
              <button onClick={() => setActiveTab('settings')} className="w-full flex items-center justify-between px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                <span>API Keys</span><KeyOutlined />
              </button>
              <button className="w-full flex items-center justify-between px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                <span>Actualizar Plan</span><SettingOutlined />
              </button>
            </div>
          </div>

          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Documentación</h3>
            <a
              href="https://docs.kubiy.com/chatbot"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            >
              <span>Ver documentación</span>
              <ExportOutlined className="text-xs" />
            </a>
          </div>
        </div>
      </div>

      {/* Delete Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title="Eliminar Chatbot"
        message={`¿Estás seguro de que quieres eliminar "${service.name}"? Esta acción no se puede deshacer y perderás todo el historial de conversaciones y configuraciones.`}
        confirmText="Eliminar"
        variant="danger"
        requireInput
        expectedInput={service.name}
        loading={isDeleting}
      />

      {/* Delete Document Modal */}
      <ConfirmationModal
        isOpen={showDeleteDocModal}
        onClose={() => { setShowDeleteDocModal(false); setDocToDelete(null); }}
        onConfirm={handleDeleteDocument}
        title="Eliminar Documento"
        message={`¿Estás seguro de que quieres eliminar "${docToDelete?.filename || ''}"? Se eliminará de la base de conocimientos y se sincronizará automáticamente.`}
        confirmText="Eliminar"
        variant="danger"
        loading={!!deletingDoc}
      />

      {/* Secret Key Generated Modal */}
      {showSecretKeyModal && generatedSecretKey && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center">
                <ExclamationCircleOutlined className="text-xl text-yellow-600 dark:text-yellow-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Secret Key Generada</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Guárdala ahora — no se podrá recuperar</p>
              </div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-2">
                <code className="flex-1 text-sm font-mono text-gray-900 dark:text-white break-all">{generatedSecretKey}</code>
                <button onClick={() => copyToClipboard(generatedSecretKey, 'Secret Key')} className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 flex-shrink-0" title="Copiar"><CopyOutlined /></button>
              </div>
            </div>
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 mb-6">
              <p className="text-xs text-yellow-800 dark:text-yellow-300">
                <strong>Importante:</strong> Esta clave no se almacena en texto plano y no podrá ser recuperada. Cópiala y guárdala en un lugar seguro. Úsala en el header <code className="bg-yellow-100 dark:bg-yellow-900 px-1 rounded">x-api-key</code> de tus peticiones server-to-server.
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => copyToClipboard(generatedSecretKey, 'Secret Key')} className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex items-center gap-2">
                <CopyOutlined />Copiar
              </button>
              <button onClick={() => { setShowSecretKeyModal(false); setGeneratedSecretKey(null); }} className="px-4 py-2 bg-black dark:bg-white text-white dark:text-black rounded-md text-sm font-medium hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors">
                Ya la guardé
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatbotDetail;